/**
 * Solana wallet adapter — Phantom / Solflare integration for
 * InstructionEnvelope::SolanaInstructions.
 *
 * The backend payload is provider-specific (Relay etc.) and may contain:
 *   - `transaction`  — base64-encoded VersionedTransaction (ready to sign)
 *   - `instructions`  — raw Solana instructions (needs assembly)
 *   - `addressLookupTables`  — optional ALT for VersionedTransaction
 *
 * @solana/web3.js is loaded dynamically (code-split) — EVM-only flows
 * never pay the ~500 KB cost.
 */

const SOLANA_DEVNET_GENESIS_HASH = "EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG"

let web3Module = null

const loadWeb3 = async () => {
  if (web3Module) return web3Module
  // webpackIgnore: @solana/web3.js resolved at runtime (installed locally or via CDN)
  web3Module = await import(/* webpackIgnore: true */ "@solana/web3.js")
  return web3Module
}

/**
 * Resolve the Solana wallet provider. Phantom and Solflare inject
 * `window.solana` (and also `window.phantom.solana` / `window.solflare.solana`).
 */
const getProvider = () => {
  if (globalThis.window?.solana?.isPhantom) {
    return globalThis.window.solana
  }
  if (globalThis.window?.phantom?.solana?.isPhantom) {
    return globalThis.window.phantom.solana
  }
  if (globalThis.window?.solflare?.isSolflare) {
    return globalThis.window.solflare
  }
  // Generic Solana Wallet Standard provider (Backpack, Glow, etc.)
  if (globalThis.window?.solana) {
    return globalThis.window.solana
  }
  return null
}

/**
 * Execute a single SolanaInstructions call.
 *
 * @param {object} call  — InstructionEnvelope::SolanaInstructions
 * @param {string} call.chain_id  — chain identifier (e.g. "solana:mainnet")
 * @param {object} call.payload   — provider-specific payload
 * @returns {{ txHash: string }}  — base58 transaction signature
 */
export const executeSolanaInstruction = async call => {
  const provider = getProvider()
  if (!provider) {
    throw new Error(
      "No Solana wallet found. Please install Phantom (phantom.app) or Solflare (solflare.com)."
    )
  }

  const { Connection, VersionedTransaction, Transaction, PublicKey } = await loadWeb3()

  // Connect to the wallet.
  try {
    await provider.connect()
  } catch (err) {
    throw new Error(`Solana wallet connection failed: ${err.message || err}`)
  }

  const { publicKey } = provider
  if (!publicKey) {
    throw new Error("Solana wallet connected but no public key returned")
  }

  const payload = typeof call.payload === "string" ? JSON.parse(call.payload) : call.payload

  // Derive a Connection from the chain_id or fall back to the wallet's own RPC.
  const connection = resolveConnection(Connection, call.chain_id)

  // --- Path A: pre-serialized VersionedTransaction (Relay, Across-Solana) ---
  if (payload.transaction) {
    const rawTx = payload.transaction
    let tx
    try {
      // Relay returns base64-encoded VersionedTransaction.
      const buf = base64ToUint8(rawTx)
      tx = VersionedTransaction.deserialize(buf)
    } catch (_err) {
      // Maybe it's base58 or we need to use Transaction.from()
      const buf = base58ToUint8(rawTx)
      try {
        tx = VersionedTransaction.deserialize(buf)
      } catch {
        tx = Transaction.from(buf)
      }
    }

    // Add address lookup tables if provided.
    if (payload.addressLookupTables && tx.message?.resolveAddressTableLookups) {
      const alts = await resolveAddressLookupTables(connection, payload.addressLookupTables)
      if (alts.length > 0) {
        tx.message.resolveAddressTableLookups(alts)
      }
    }

    const result = await provider.signAndSendTransaction(tx)
    return { txHash: result.signature?.toString?.() || result.signature }
  }

  // --- Path B: raw instructions (provider-agnostic) ---
  if (payload.instructions) {
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed")
    const tx = new Transaction({
      feePayer: publicKey,
      blockhash,
      lastValidBlockHeight,
    })

    for (const ix of payload.instructions) {
      const keys = (ix.keys || []).map(k => ({
        pubkey: new PublicKey(k.pubkey),
        isSigner: k.isSigner ?? false,
        isWritable: k.isWritable ?? true,
      }))
      tx.add({
        keys,
        programId: new PublicKey(ix.programId),
        data: base64ToUint8(ix.data || ""),
      })
    }

    const result = await provider.signAndSendTransaction(tx)
    return { txHash: result.signature?.toString?.() || result.signature }
  }

  throw new Error(
    "Solana payload has neither 'transaction' nor 'instructions'. " +
    "The provider response schema may need PoC calibration."
  )
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const resolveConnection = (Connection, chainId) => {
  const rpcForDevnet = "https://api.devnet.solana.com"
  const rpcForMainnet = "https://api.mainnet-beta.solana.com"
  const rpc = chainId?.includes("mainnet") || chainId?.includes("4sgjm") ? rpcForMainnet : rpcForDevnet
  return new Connection(rpc, "confirmed")
}

const base64ToUint8 = str => {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(str, "base64"))
  }
  const binary = globalThis.atob(str)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

const base58ToUint8 = str => {
  // Minimal base58 decoder using the Solana alphabet.
  const alphabet = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz"
  const lookup = {}
  for (let i = 0; i < alphabet.length; i++) {
    lookup[alphabet[i]] = BigInt(i)
  }
  let value = 0n
  for (const c of str) {
    value = value * 58n + (lookup[c] ?? 0n)
  }
  // Convert BigInt to byte array (big-endian).
  const bytes = []
  while (value > 0n) {
    bytes.unshift(Number(value & 0xffn))
    value >>= 8n
  }
  // Handle leading zeros (each '1' in base58 = 0x00 in bytes).
  let leading = 0
  while (str[leading] === "1") leading++
  return new Uint8Array([...new Array(leading).fill(0), ...bytes])
}

/**
 * Resolve address lookup tables from the payload.
 * Each entry: { accountKey: string, addresses: string[] }
 */
const resolveAddressLookupTables = async (connection, entries) => {
  const { PublicKey, AddressLookupTableAccount } = web3Module
  const alts = []
  for (const entry of entries) {
    try {
      const accountKey = new PublicKey(entry.accountKey)
      const alt = await connection.getAddressLookupTable(accountKey)
      if (alt?.value) {
        alts.push(alt.value)
      } else {
        // Build a minimal ALT from the addresses when the chain doesn't have it.
        alts.push(
          new AddressLookupTableAccount({
            key: accountKey,
            state: AddressLookupTableAccount.deserialize(
              serializeAltAddresses(entry.addresses || [])
            ),
          })
        )
      }
    } catch (_err) {
      // ALT not available on-chain — skip.
    }
  }
  return alts
}

const serializeAltAddresses = () => {
  // For now, return empty — full ALT deserialization needs the account data
  // format. The on-chain lookup path (connection.getAddressLookupTable) is
  // the primary path and works correctly.
  return new Uint8Array(0)
}

// Re-export the Phantom provider for binding from ReScript.
export const getSolanaProvider = () => getProvider()

// ---------------------------------------------------------------------------
// Solana Pay (native non-custodial crypto) — connect + sign a backend-built tx
// ---------------------------------------------------------------------------

/**
 * Connect the injected Solana wallet (Phantom / Solflare / Backpack …) and
 * return the buyer's base58 public key.  Used by the SDK to POST `{ account }`
 * to the backend Solana Pay transaction endpoint.
 *
 * @returns {{ account: string }}
 */
export const connectSolanaWallet = async () => {
  const provider = getProvider()
  if (!provider) {
    throw new Error(
      "No Solana wallet found. Please install Phantom (phantom.app), Solflare (solflare.com), or Backpack."
    )
  }
  try {
    await provider.connect()
  } catch (err) {
    throw new Error(`Solana wallet connection failed: ${err.message || err}`)
  }
  const { publicKey } = provider
  if (!publicKey) {
    throw new Error("Solana wallet connected but no public key returned")
  }
  return { account: publicKey.toString() }
}

/**
 * Sign and send a backend-built base64-encoded unsigned Solana transaction
 * (the Solana Pay split-payment + reference, built by
 * `POST /channels/non-custodial-crypto/solana/pay/{invoice_id}`).
 *
 * The backend serializes the transaction with a *placeholder* (zero) blockhash
 * because it can't know a recent one at build time, so we MUST overwrite the
 * recentBlockhash and feePayer with live values before the wallet signs.
 *
 * @param {string} base64Tx   — base64-encoded unsigned legacy/versioned tx
 * @param {string} [chainId]  — chain hint ("solana" | "solana-devnet" | "solana:mainnet" …)
 * @returns {{ txHash: string }}  — base58 transaction signature
 */
export const signAndSendBase64Tx = async (base64Tx, chainId) => {
  const provider = getProvider()
  if (!provider) {
    throw new Error(
      "No Solana wallet found. Please install Phantom (phantom.app), Solflare (solflare.com), or Backpack."
    )
  }

  const { Connection, VersionedTransaction, Transaction } = await loadWeb3()

  // Ensure the wallet is connected and we have the buyer's public key.
  try {
    await provider.connect()
  } catch (err) {
    throw new Error(`Solana wallet connection failed: ${err.message || err}`)
  }
  const { publicKey } = provider
  if (!publicKey) {
    throw new Error("Solana wallet connected but no public key returned")
  }

  const connection = resolveConnection(Connection, chainId)
  const buf = base64ToUint8(base64Tx)

  // The backend builds a legacy transaction (serialize_transaction); try legacy
  // first, then fall back to a VersionedTransaction for forward-compat.
  let tx
  let isVersioned = false
  try {
    tx = Transaction.from(buf)
  } catch (_legacyErr) {
    try {
      tx = VersionedTransaction.deserialize(buf)
      isVersioned = true
    } catch (versionedErr) {
      throw new Error(
        `Failed to deserialize Solana transaction: ${versionedErr.message || versionedErr}`
      )
    }
  }

  // Refresh the (placeholder) blockhash with a live one and set the fee payer.
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed")
  if (isVersioned) {
    // VersionedTransaction: patch the message's recentBlockhash in place.
    if (tx.message) {
      tx.message.recentBlockhash = blockhash
    }
  } else {
    tx.recentBlockhash = blockhash
    tx.lastValidBlockHeight = lastValidBlockHeight
    if (!tx.feePayer) {
      tx.feePayer = publicKey
    }
  }

  // Let the wallet sign + broadcast. signAndSendTransaction is supported by
  // Phantom, Solflare and Backpack; on success it returns { signature }.
  const result = await provider.signAndSendTransaction(tx)
  const signature = result?.signature?.toString?.() || result?.signature || result
  if (!signature) {
    throw new Error("Solana wallet returned no transaction signature")
  }
  return { txHash: typeof signature === "string" ? signature : signature.toString() }
}
